import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import { axiosInstance } from "../../../utils/axios";
import { toast } from "react-toastify";
import Joi from "joi";
import PropTypes from "prop-types";
import { getUser } from "../../../utils/storage";
import Button from "../../../components/Button";
import { CheckIcon } from "@heroicons/react/20/solid";

const MARKET_TYPE_OPTIONS = [
  { value: "CH", label: "Switzerland (CH)" },
  { value: "EX", label: "International (EX)" },
];

export default function ClientForm({ payload = null, onSuccess }) {
  const user = getUser();
  const [formData, setFormData] = useState({
    id: payload?.id || null,
    name: payload?.name || "",
    unique_id: payload?.unique_id || "",
    market_type: payload?.market_type || "CH",
    fixed_price: Boolean(payload?.fixed_price),
    user_id: user.id,
  });
  const [errors, setErrors] = useState({});

  // Update form data when payload changes
  useEffect(() => {
    if (payload) {
      setFormData({
        id: payload.id || null,
        name: payload.name || "",
        unique_id: payload.unique_id || "",
        market_type: payload.market_type || "CH",
        fixed_price: Boolean(payload.fixed_price),
        user_id: user.id,
      });
    }
  }, [payload, user.id]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    if (name === "unique_id") {
      // Remove any non-alphanumeric characters
      let cleanedValue = value.replace(/[^A-Za-z0-9]/g, "");

      // If the value doesn't start with 'A', add it
      if (cleanedValue && !cleanedValue.startsWith("A")) {
        cleanedValue = "A" + cleanedValue.replace(/^A/i, "");
      }

      // Convert to uppercase
      cleanedValue = cleanedValue.toUpperCase();

      setFormData((prevData) => ({ ...prevData, [name]: cleanedValue }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }

    // Clear error for the field being changed
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateFormData = () => {
    const schema = Joi.object({
      id: Joi.number().allow(null),
      name: Joi.string().min(2).required().messages({
        "string.empty": "Name is required",
        "string.min": "Name must be at least 2 characters long",
      }),
      unique_id: Joi.string()
        .pattern(/^A\d+$/)
        .required()
        .messages({
          "string.empty": "Unique ID is required",
          "string.pattern.base":
            "Unique ID must start with 'A' followed by numbers",
        }),
      market_type: Joi.string().valid("CH", "EX").required().messages({
        "any.only": "Please select a valid market type",
        "string.empty": "Market type is required",
        "any.required": "Market type is required",
      }),
      fixed_price: Joi.boolean(),
      user_id: Joi.number().required().messages({
        "any.required": "User ID is required",
      }),
    });

    const { error } = schema.validate(formData, { abortEarly: false });
    if (error) {
      const newErrors = {};
      error.details.forEach((detail) => {
        newErrors[detail.path[0]] = detail.message;
      });
      setErrors(newErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateFormData()) {
      console.log("Errors:", errors);
      toast.error("Please correct the errors in the form.");
      return;
    }

    try {
      const data = {
        id: formData.id,
        name: formData.name,
        unique_id: formData.unique_id,
        market_type: formData.market_type,
        fixed_price: formData.fixed_price,
        user_id: formData.user_id,
      };

      if (formData.id) {
        await axiosInstance.put(
          `${window.REACT_APP_API_ENDPOINT}/clients/${formData.id}`,
          data
        );
      } else {
        await axiosInstance.post(
          window.REACT_APP_API_ENDPOINT + "/clients",
          data
        );
      }
      toast.success("Client saved successfully!");
      onSuccess?.();
    } catch (error) {
      toast.error(
        `Failed to save client. Please try again. Error: ${error.response.data.message}`
      );
      console.error("Client save error:", error);
    }
  };

  const inputClassName = (fieldName) => `
    block w-full rounded-md border-0 py-3 px-4 text-gray-900 shadow-sm ring-1 ring-inset 
    ${
      errors[fieldName]
        ? "ring-red-300 focus:ring-red-500"
        : "ring-gray-300 focus:ring-indigo-600"
    } 
    placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6
  `;

  return (
    <form className="w-full space-y-6" onSubmit={handleFormSubmit}>
      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Name
        </label>
        <div className="relative mt-2">
          <input
            id="name"
            name="name"
            type="text"
            autoComplete="name"
            placeholder="Enter client name"
            value={formData.name}
            onChange={handleFormChange}
            className={inputClassName("name")}
          />
          {errors.name && (
            <p className="mt-2 text-sm text-red-600">{errors.name}</p>
          )}
        </div>
      </div>

      <div>
        <label
          htmlFor="unique_id"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Unique ID
        </label>
        <div className="relative mt-2">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center px-3 border-[1px] rounded-l-md bg-blue-50 border-blue-300 pointer-events-none">
              <span className="text-blue-500">A</span>
            </div>
            <input
              type="text"
              name="unique_id"
              id="unique_id"
              className={`${inputClassName("unique_id")} pl-12`}
              placeholder="Enter numbers after A"
              value={formData.unique_id.replace(/^A/, "")}
              onChange={handleFormChange}
            />
          </div>
          {errors.unique_id && (
            <p className="mt-2 text-sm text-red-600">{errors.unique_id}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            Must start with 'A' followed by numbers
          </p>
        </div>
      </div>

      <div>
        <label
          htmlFor="market_type"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Market Type
        </label>
        <div className="relative mt-2">
          <select
            id="market_type"
            name="market_type"
            value={formData.market_type}
            onChange={handleFormChange}
            className={inputClassName("market_type")}
          >
            <option value="" disabled>Select market type</option>
            {MARKET_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.market_type && (
            <p className="mt-2 text-sm text-red-600">{errors.market_type}</p>
          )}
        </div>
      </div>

      <div className="flex items-start gap-3">
        <input
          id="fixed_price"
          name="fixed_price"
          type="checkbox"
          checked={!!formData.fixed_price}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, fixed_price: e.target.checked }))
          }
          className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
        />
        <label htmlFor="fixed_price" className="text-sm leading-6 text-gray-900">
          <span className="font-medium">Fixed-price client</span>
          <span className="block text-gray-500">
            Bill a flat monthly rental only — omit the B/W and color counter lines
            from AbaNinja invoice descriptions.
          </span>
        </label>
      </div>

      <div>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          icon={<CheckIcon className="h-4 w-4" />}
        >
          Save
        </Button>
      </div>
    </form>
  );
}

ClientForm.propTypes = {
  payload: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    unique_id: PropTypes.string,
    market_type: PropTypes.string,
    fixed_price: PropTypes.bool,
  }),
  onSuccess: PropTypes.func,
};

ClientForm.defaultProps = {
  payload: null,
  onSuccess: () => {},
};